/**
 * Monolith Identifier Executor
 *
 * Handles the 'monolith-identifier' node execution.
 * Calls the ModernizeIT API Layer /monolithidentifier endpoint.
 *
 * API Flow:
 * 1. POST /monolithidentifier → returns job_id and results
 * 2. If status is 'running', poll GET /monolithidentifier/{job_id}/status
 * 3. GET /monolithidentifier/{job_id}/results → retrieve full results
 *
 * This node analyzes:
 * - COBOL source for monolithic patterns (from ingested source)
 * - Java source for monolithic patterns (from code_analysis generated code)
 *
 * 5-Step Analysis Flow:
 * 1. Static analysis (COBOL or Java)
 * 2. Pattern detection (God Objects, Big Ball of Mud, etc.)
 * 3. Modularity calculation (cohesion, coupling, complexity)
 * 4. Business capability analysis
 * 5. Decomposition strategy generation
 *
 * Artifacts produced:
 * - static_analysis.json
 * - detected_patterns.json
 * - modularity_metrics.json
 * - business_capabilities.json
 * - decomposition_strategy.json
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the monolith identifier node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing monolith analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // Get source type from config (defaults to 'cobol')
  const sourceType = config.analysis_type || 'cobol'

  // 1. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
    source_type: sourceType,
  }

  onProgress(5, `Submitting ${sourceType} monolith analysis job...`)

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/monolithidentifier`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Monolith identification failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Monolith analysis complete')
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, 'Processing monolith analysis...')
    const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-95%
        const mappedProgress = 15 + (progress * 0.80)
        onProgress(mappedProgress, message)
      },
      signal
    })

    // Merge completed job data
    Object.assign(jobResponse, completedJob)
  }

  onProgress(100, 'Monolith analysis complete')

  // 5. Return formatted result with job data
  return formatResult(jobId, sourceType, jobResponse)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, sourceType, data) {
  return {
    // Job tracking
    jobs: {
      monolithidentifier_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Source info
    source_type: sourceType,

    // Paths
    source_path: data.source_path,
    artifacts_path: data.artifacts_path,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Summary statistics
    summary: data.summary || {},

    // Artifacts produced
    artifacts: {
      json: data.json_artifacts || [],
      static_analysis: data.artifacts?.static_analysis,
      detected_patterns: data.artifacts?.detected_patterns,
      modularity_metrics: data.artifacts?.modularity_metrics,
      business_capabilities: data.artifacts?.business_capabilities,
      decomposition_strategy: data.artifacts?.decomposition_strategy,
    },

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
